import type { CentralChatContext, CentralChatContextType } from './types';

const CONTEXT_TYPES: CentralChatContextType[] = [
  'global',
  'wordpress',
  'editor_article',
  'template_builder',
  'service_page',
  'section',
  'technical_audit_issue',
];

export function buildCentralChatContext(
  params: {
    tenantId: string | null;
    pathname: string;
    searchParams: URLSearchParams;
    overrideContextType?: CentralChatContextType;
  },
): CentralChatContext {
  const explicit = params.overrideContextType || validContextType(params.searchParams.get('context_type'));
  const route = params.searchParams.get('route') || params.pathname;
  const contextType = explicit || inferContextType(route);
  return {
    context_type: contextType,
    tenant_id: params.tenantId,
    route,
    content_id: params.searchParams.get('content_id') || extractRouteId(route, /\/(?:content\/editor|blog)\/([^/?#]+)/) || extractRouteId(route, /\/editor\/[^/?#]+\/([^/?#]+)/),
    template_id: params.searchParams.get('template_id') || undefined,
    section_id: params.searchParams.get('section_id') || undefined,
    issue_id: params.searchParams.get('issue_id') || undefined,
    selected_text: params.searchParams.get('selected_text') || undefined,
    capabilities: {
      can_use_wordpress: contextType === 'wordpress' || contextType === 'global',
      can_edit_article: contextType === 'editor_article',
      can_generate_service_page: contextType === 'service_page' || contextType === 'global',
      can_render_arrow_ui: true,
      can_use_web_research: true,
      can_generate_images: true,
    },
    inputs: inputParams(params.searchParams),
  };
}

function inferContextType(route: string): CentralChatContextType {
  if (route.startsWith('/editor/') || route.startsWith('/content/editor/') || route.startsWith('/blog/')) {
    return 'editor_article';
  }
  if (route.startsWith('/content/templates')) return 'template_builder';
  if (route.startsWith('/pages/service') || route.startsWith('/pages/generate')) return 'service_page';
  return 'global';
}

function extractRouteId(route: string, pattern: RegExp): string | undefined {
  return route.match(pattern)?.[1];
}

function validContextType(value: string | null): CentralChatContextType | undefined {
  return CONTEXT_TYPES.includes(value as CentralChatContextType)
    ? (value as CentralChatContextType)
    : undefined;
}

function inputParams(searchParams: URLSearchParams): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  searchParams.forEach((value, key) => {
    if (!key.startsWith('input_')) return;
    inputs[key.slice('input_'.length)] = value;
  });
  return inputs;
}
